import React, { FunctionComponent, useState } from 'react'
import { Field, Form } from 'react-final-form'
import { FormattedMessage, useIntl } from 'react-intl'

import { addMinutes, isValid, parse } from 'date-fns'
import { format, zonedTimeToUtc } from 'date-fns-tz'
import { isEmpty, range, toNumber } from 'lodash-es'
import styled, { useTheme } from 'styled-components/native'

import { thisFieldIsRequiredErrorMessage } from '@lyrahealth-inc/shared-app-logic/src/features/common/constants/messages'
import { Appointment } from '@lyrahealth-inc/shared-app-logic/src/models/appointment/Appointment'

import { BodyText } from '../../atoms/bodyText/BodyText'
import { Display } from '../../atoms/display/Display'
import { FormContainer } from '../../atoms/formElements/formUtils'
import { SelectFieldRFF } from '../../atoms/formElements/SelectField'
import { PrimaryButton } from '../../atoms/primaryButton/PrimaryButton'
import { SecondaryButton } from '../../atoms/secondaryButton/SecondaryButton'
import { Title } from '../../atoms/title/Title'
import { useMediaQuerySize } from '../../hooks/useMediaQuerySize'
import { BodyTextSize, DisplaySize, TitleSize } from '../../styles/typeStyles'
import { ThemeType } from '../../utils/themes/ThemeProvider'
import { tID } from '../../utils/utils'

export enum BookingSteps {
  SCHEDULE = 'schedule',
}

export interface SessionDetailsProps {
  appointment?: Appointment
  appointmentDurationOptions?: number[]
  appointmentType: string
  displayAppointmentDuration: boolean
  displayRepeatingFields: boolean
  headerBanner?: React.ReactNode
  initialValues?: SessionDetailsFormValues
  meetingFormats?: { value: string; text: string }[]
  nextStep?: BookingSteps | null
  onCancel: () => void
  onSubmitForm: (values: SessionDetailsFormValues) => void
  partyName: string
  sessionFrequencyOptions?: { label: string; value: number }[]
  submitting?: boolean
  timeZone?: string
  customSubmitTextElement?: string
  handleSubmitRef?: React.MutableRefObject<(() => void) | undefined>
  hideSubmitButton?: boolean
  hideTitle?: boolean
  meetingFormatLabel?: React.ReactNode
  sessionTypeOptions?: { helperText?: string; label: string; value: string }[]
  title?: React.ReactNode
}

export type SessionDetailsFormValues = {
  appointmentDuration?: string
  meetingFormat?: string
  sessionFrequency?: string
  repeatFor?: string
  appointmentStatus?: string
  sessionType?: string
}

const FieldsContainer = styled.View<{ theme: ThemeType }>(({ theme: { spacing } }) => ({
  flexDirection: 'row',
  flexWrap: 'wrap',
  marginTop: spacing['32px'],
}))

const TitleContainer = styled.View<{ theme: ThemeType }>(({ theme: { spacing } }) => ({
  paddingHorizontal: spacing['16px'],
}))

const HeaderBannerContainer = styled.View<{ theme: ThemeType }>(({ theme: { spacing } }) => ({
  marginTop: spacing['24px'],
  paddingHorizontal: spacing['16px'],
}))

const AppointmentInfoContainer = styled.View<{ theme: ThemeType }>(({ theme: { spacing } }) => ({
  marginTop: spacing['32px'],
  paddingHorizontal: spacing['16px'],
}))

const AppointmentDurationContainer = styled.View<{ theme: ThemeType }>(({ theme: { spacing } }) => ({
  marginTop: spacing['24px'],
}))

const FieldContainer = styled.View<{ theme: ThemeType; isMinWidthTablet: boolean }>(
  ({ theme: { spacing }, isMinWidthTablet }) => ({
    width: isMinWidthTablet ? '50%' : '100%',
    paddingHorizontal: spacing['16px'],
  }),
)

const SessionTypeHelperText = styled(BodyText)<{ theme: ThemeType }>(({ theme: { spacing } }) => ({
  marginTop: spacing['4px'],
}))

const SessionTypeFieldContent = styled.View<{ theme: ThemeType }>(({ theme: { spacing } }) => ({
  marginBottom: spacing['40px'],
}))

const ButtonContainer = styled.View<{ theme: ThemeType; isMinWidthTablet: boolean }>(
  ({ theme: { spacing }, isMinWidthTablet }) => ({
    width: '100%',
    paddingHorizontal: spacing['16px'],
    display: 'flex',
    flexDirection: isMinWidthTablet ? 'row' : 'column',
    gap: spacing['16px'],
  }),
)

export const SessionDetails: FunctionComponent<SessionDetailsProps> = ({
  appointment,
  appointmentDurationOptions = [25, 50, 80],
  appointmentType,
  displayAppointmentDuration,
  displayRepeatingFields,
  headerBanner,
  initialValues = {
    appointmentDuration: '50',
    meetingFormat: 'video',
    sessionFrequency: '0',
  },
  meetingFormats = [],
  nextStep,
  onCancel,
  onSubmitForm,
  partyName,
  sessionFrequencyOptions = [
    { label: 'Does not repeat', value: 0 },
    { label: 'Repeat every two weeks', value: 14 },
  ],
  submitting,
  timeZone,
  customSubmitTextElement,
  handleSubmitRef,
  hideSubmitButton = false,
  hideTitle = false,
  meetingFormatLabel,
  sessionTypeOptions = [],
  title,
}) => {
  const { formatDate, formatTime, formatMessage } = useIntl()
  const { colors } = useTheme()
  const { isMinWidthTablet } = useMediaQuerySize()
  const [sessionFrequency, setSessionFrequency] = useState<string | undefined>(initialValues.sessionFrequency)
  const [repeatFor, setRepeatFor] = useState<string | undefined>(undefined)
  const [isRepeatForDisabled, setIsRepeatForDisabled] = useState(true)
  const userTimeZone =
    timeZone ?? String(format(new Date(), 'zzz', { timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone }))
  let appointmentStartInLocalTime: Date, appointmentDuration: string

  if (!isEmpty(appointment) && !isEmpty(appointment.startDate)) {
    appointmentStartInLocalTime = zonedTimeToUtc(
      parse(`${appointment.startDate} ${appointment.startTime}`, 'yyyy-MM-dd HH:mm:ss', new Date()),
      appointment.timeZone,
    )
    const appointmentEndInLocalTime = addMinutes(appointmentStartInLocalTime, 60)
    appointmentDuration = displayAppointmentDuration
      ? formatTime(appointmentStartInLocalTime, {
          hour: 'numeric',
          minute: 'numeric',
          ...(timeZone && { timeZone }),
        }) + ` ${userTimeZone}`
      : formatTime(appointmentStartInLocalTime, {
          hour: 'numeric',
          minute: 'numeric',
          ...(timeZone && { timeZone }),
        }) +
        ' - ' +
        formatTime(appointmentEndInLocalTime, {
          hour: 'numeric',
          minute: 'numeric',
          ...(timeZone && { timeZone }),
        }) +
        ` ${userTimeZone}`
  }

  const validateForm = (values: SessionDetailsFormValues) => {
    const errors: Partial<Record<keyof SessionDetailsFormValues, string>> = {}
    if (displayAppointmentDuration && !values.appointmentDuration) {
      errors.appointmentDuration = formatMessage(thisFieldIsRequiredErrorMessage)
    }
    if (meetingFormats.length && !values.meetingFormat) {
      errors.meetingFormat = formatMessage(thisFieldIsRequiredErrorMessage)
    }
    if (displayRepeatingFields) {
      if (!sessionFrequency) {
        errors.sessionFrequency = formatMessage(thisFieldIsRequiredErrorMessage)
      } else if (toNumber(sessionFrequency) >= 2 && !repeatFor) {
        errors.repeatFor = formatMessage(thisFieldIsRequiredErrorMessage)
      }
    }
    if (sessionTypeOptions.length > 0 && !values.sessionType) {
      errors.sessionType = formatMessage(thisFieldIsRequiredErrorMessage)
    }

    return errors
  }

  const onSubmit = (values: SessionDetailsFormValues) => {
    const payload = {
      appointmentDuration: values.appointmentDuration,
      ...(initialValues.appointmentStatus && { appointmentStatus: initialValues.appointmentStatus }),
      meetingFormat: values.meetingFormat,
      sessionFrequency,
      repeatFor: isRepeatForDisabled ? '0' : repeatFor,
      ...(values.sessionType && { sessionType: values.sessionType }),
    }
    onSubmitForm(payload)
  }

  const onFrequencyChange = (e: string) => {
    setSessionFrequency(e)
    const newSessionFrequencyDays = e ? parseInt(e) : 0
    if (newSessionFrequencyDays > 0) {
      setRepeatFor('2')
      setIsRepeatForDisabled(false)
    } else {
      setRepeatFor('')
      setIsRepeatForDisabled(true)
    }
  }

  return (
    <Form
      onSubmit={onSubmit}
      initialValues={initialValues}
      validate={validateForm}
      render={({ handleSubmit, values }) => {
        if (handleSubmitRef) {
          handleSubmitRef.current = handleSubmit
        }
        const selectedSessionType = sessionTypeOptions.find((option) => option.value === values.sessionType)
        return (
          <FormContainer handleSubmit={handleSubmit}>
            {!hideTitle && (
              <TitleContainer testID={tID('SessionDetails-titleContainer')}>
                <Display
                  size={DisplaySize.SMALL}
                  text={
                    title ??
                    (isEmpty(appointment) ? (
                      <FormattedMessage
                        defaultMessage='Create {appointmentType, select,
                        new {new}
                        rescheduled {rescheduled}
                        other {new}
                      } session with {partyName}'
                        description='Header when scheduling an appointment'
                        values={{
                          appointmentType,
                          partyName,
                        }}
                      />
                    ) : (
                      <FormattedMessage
                        defaultMessage='Confirm {appointmentType, select,
                        new {new}
                        rescheduled {rescheduled}
                        other {new}
                      } session with {partyName}'
                        description='Header when confirming an appointment'
                        values={{
                          appointmentType,
                          partyName,
                        }}
                      />
                    ))
                  }
                />
              </TitleContainer>
            )}
            {headerBanner && <HeaderBannerContainer>{headerBanner}</HeaderBannerContainer>}
            {!isEmpty(appointment) && isValid(appointmentStartInLocalTime) && (
              <AppointmentInfoContainer>
                <Title
                  size={TitleSize.MEDIUM}
                  text={formatDate(appointmentStartInLocalTime, {
                    weekday: 'long',
                    month: 'long',
                    day: '2-digit',
                    timeZone,
                  })}
                />
                <AppointmentDurationContainer>
                  <Title size={TitleSize.MEDIUM} text={appointmentDuration} />
                </AppointmentDurationContainer>
              </AppointmentInfoContainer>
            )}
            <FieldsContainer>
              {displayAppointmentDuration && (
                <FieldContainer isMinWidthTablet={isMinWidthTablet}>
                  <Field
                    name='appointmentDuration'
                    component={SelectFieldRFF}
                    label={formatMessage({
                      defaultMessage: 'Duration',
                      description: 'label for duration selection field',
                    })}
                    options={appointmentDurationOptions.map((number: number) => {
                      return { label: `${number} minutes`, value: number }
                    })}
                  />
                </FieldContainer>
              )}
              {meetingFormats.length > 0 && (
                <FieldContainer isMinWidthTablet={isMinWidthTablet}>
                  <Field
                    name='meetingFormat'
                    component={SelectFieldRFF}
                    label={
                      meetingFormatLabel ??
                      formatMessage({
                        defaultMessage: 'Session Type',
                        description: 'label for session type selection field',
                      })
                    }
                    options={meetingFormats.map((option: { value: string; text: string }) => {
                      return { label: option.text, value: option.value }
                    })}
                  />
                </FieldContainer>
              )}
              {displayRepeatingFields && (
                <>
                  <FieldContainer isMinWidthTablet={isMinWidthTablet}>
                    <Field
                      name='sessionFrequency'
                      component={SelectFieldRFF}
                      label={formatMessage({
                        defaultMessage: 'Frequency',
                        description: 'label for session frequency selection field',
                      })}
                      options={sessionFrequencyOptions}
                      input={{
                        onChange: onFrequencyChange,
                      }}
                      disablePlaceholder
                    />
                  </FieldContainer>
                  <FieldContainer isMinWidthTablet={isMinWidthTablet}>
                    <Field
                      name='repeatFor'
                      component={SelectFieldRFF}
                      input={{
                        onChange: (e: string) => setRepeatFor(e),
                        value: repeatFor,
                      }}
                      disabled={isRepeatForDisabled}
                      label={formatMessage({
                        defaultMessage: 'Repeat For',
                        description: 'label for number of session repeats',
                      })}
                      options={range(2, 9).map((number: number) => {
                        return { label: `${number} sessions`, value: number }
                      })}
                    />
                  </FieldContainer>
                </>
              )}
              {sessionTypeOptions.length > 0 && (
                <FieldContainer isMinWidthTablet={isMinWidthTablet}>
                  <SessionTypeFieldContent>
                    <Field
                      name='sessionType'
                      component={SelectFieldRFF}
                      label={formatMessage({
                        defaultMessage: 'Session type',
                        description: 'Label for the DBT Teens clinical session type selection field',
                      })}
                      options={sessionTypeOptions}
                      required
                      baseInputStyle={{ marginBottom: 0 }}
                    />
                    {selectedSessionType?.helperText && (
                      <SessionTypeHelperText
                        testID={tID('SessionDetails-session-type-attendees')}
                        text={selectedSessionType.helperText}
                        size={BodyTextSize.SMALL}
                        color={colors.textSecondary}
                      />
                    )}
                  </SessionTypeFieldContent>
                </FieldContainer>
              )}
              {!hideSubmitButton && (
                <ButtonContainer isMinWidthTablet={isMinWidthTablet}>
                  <PrimaryButton
                    text={
                      nextStep === BookingSteps.SCHEDULE
                        ? formatMessage({
                            defaultMessage: 'Select timeslot',
                            description: 'button label to select a session timeslot',
                          })
                        : customSubmitTextElement
                          ? customSubmitTextElement
                          : formatMessage({
                              defaultMessage: 'Confirm Session',
                              description: 'button label to confirm a session timeslot',
                            })
                    }
                    disabled={submitting}
                    loading={submitting}
                    onPress={handleSubmit}
                    fullWidth={!isMinWidthTablet}
                    testID={tID('SessionDetails-confirm-session')}
                  />
                  {nextStep !== BookingSteps.SCHEDULE && (
                    <SecondaryButton
                      text={formatMessage({
                        defaultMessage: 'Cancel Session',
                        description: 'button label to cancel selected session',
                      })}
                      onPress={onCancel}
                      fullWidth={!isMinWidthTablet}
                      testID={tID('SessionDetails-cancel-session')}
                    />
                  )}
                </ButtonContainer>
              )}
            </FieldsContainer>
          </FormContainer>
        )
      }}
    />
  )
}
